import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// アプリ最上位のエラーバウンダリ。
// 単一コンポーネントの描画時例外が発生しても全画面が真っ白（白飛び）にならないよう、
// 最低限のフォールバック UI を表示する。i18n に依存しない素の文言にして、
// i18n 初期化前後どちらで落ちても確実に描画できるようにする。
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 原因調査用にコンソールへ残す（本番でも devtools から確認できる）。
    console.error("App crashed (caught by ErrorBoundary):", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            background: "#FAF5E8",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            textAlign: "center",
            fontFamily: "'Noto Sans JP', system-ui, sans-serif",
            color: "#1A1A1A",
          }}
        >
          <p style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
            問題が発生しました / Something went wrong
          </p>
          <p style={{ fontSize: 14, color: "#666", marginBottom: 24, lineHeight: 1.8 }}>
            ページの読み込み中にエラーが発生しました。お手数ですが再読み込みしてください。
            <br />
            An error occurred while loading the page. Please reload.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "10px 24px",
              borderRadius: 10,
              border: "none",
              background: "#2C5F4F",
              color: "#ffffff",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            再読み込み / Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
